/* eslint-disable max-lines */
import { retenoEventsApi, userApi } from 'api'
import { APP_NAME, LEARNWORLDS_DEEP_LINK_BASE } from 'app-config'
import i18n from 'i18next'

import {
  setAnswersFromBackendAction,
  setErrorAction,
  setUtmTags,
  startFetching,
  stopFetching,
} from 'root-redux/actions/common'
import { selectAnswers, selectUtmTags } from 'root-redux/selects/common'
import { selectUUID } from 'root-redux/selects/user'

import { addTokenToLink } from 'helpers/addTokenToLink'
import { captureFunnelError } from 'helpers/captureFunnelError'
import { IFacebookParams } from 'helpers/createFacebookParams'
import { FunnelErrorCode } from 'helpers/funnelErrors'
import { getAccountStatusFromRawStatus } from 'helpers/getAccountStatusFromRawStatus'
import { getCookie } from 'helpers/getCookie'
import { getSha256Hash } from 'helpers/getSha256Hash'
import { getUserStatusFromRawUserStatus } from 'helpers/getUserStatusFromRawUserStatus'

import { TAnswers, TUtmTags } from 'models/common.model'
import {
  IAction,
  IAppState,
  TAppActionThunk,
  TAppDispatchThunk,
} from 'models/store.model'
import {
  IAccountStatus,
  IGetUUIDResponseRaw,
  IUserStatus,
  TUserConfig,
} from 'models/user.model'

import { eventLogger } from 'services/eventLogger.service'
import { RetenoEvents, retenoLogger } from 'services/retenoLogger.service'

import { CustomPageId } from 'page-constants'
import { APP_OUTER_LINKS } from 'root-constants'

const MODULE_NAME = 'USER'

export const SET_UUID = `${MODULE_NAME}/SET_UUID`
export const GET_STATUS = `${MODULE_NAME}/GET_STATUS`
export const SET_STATUS = `${MODULE_NAME}/SET_STATUS`
export const SET_TEST_ENVIRONMENT_QUERY_PARAM = `${MODULE_NAME}/SET_TEST_ENVIRONMENT_QUERY_PARAM`
export const SEND_USER_ANSWERS = `${MODULE_NAME}/SEND_USER_ANSWERS`
export const SEND_USER_CONFIG = `${MODULE_NAME}/SEND_USER_CONFIG`
export const UPDATE_USER_CONFIG = `${MODULE_NAME}/UPDATE_USER_CONFIG`
export const SEND_FACEBOOK_PARAMS = `${MODULE_NAME}/SEND_FACEBOOK_PARAMS`
const SEND_USER_TTCLID = `${MODULE_NAME}/SEND_USER_TTCLID`
const SEND_USER_SCCID = `${MODULE_NAME}/SEND_USER_SCCID`
const SEND_USER_RDTCID = `${MODULE_NAME}/SEND_USER_RDTCID`
export const GET_ONE_LINK = `${MODULE_NAME}/GET_ONE_LINK`
export const SET_ONE_LINK = `${MODULE_NAME}/SET_ONE_LINK`
export const GET_LEARNWORLDS_LINK = `${MODULE_NAME}/GET_LEARNWORLDS_LINK`
export const SET_LEARNWORLDS_LINK = `${MODULE_NAME}/SET_LEARNWORLDS_LINK`
export const SET_ACCOUNT_CREATION_FLOW_PENDING = `${MODULE_NAME}/SET_ACCOUNT_CREATION_FLOW_PENDING`
export const SEND_USER_EMAIL = `${MODULE_NAME}/SEND_USER_EMAIL`
export const SEND_USER_EMAIL_CONSENT = `${MODULE_NAME}/SEND_USER_EMAIL_CONSENT`
export const SEND_USER_LEARNWORLDS_ACCOUNT = `${MODULE_NAME}/SEND_USER_LEARNWORLDS_ACCOUNT`
export const GET_ACCOUNT_STATUS = `${MODULE_NAME}/GET_ACCOUNT_STATUS`
export const SET_ACCOUNT_STATUS = `${MODULE_NAME}/SET_ACCOUNT_STATUS`
export const RESEND_INSTRUCTIONS = `${MODULE_NAME}/RESEND_INSTRUCTIONS`
const BIND_USER = `${MODULE_NAME}/BIND_USER`
const SEND_USER_EPIK = `${MODULE_NAME}/SEND_USER_EPIK`

export function setUUIDAction(uuid: string): IAction<string> {
  return {
    type: SET_UUID,
    payload: uuid,
  }
}

export const setAccountStatusAction = (
  accountStatus: IAccountStatus,
): IAction<IAccountStatus> => ({
  type: SET_ACCOUNT_STATUS,
  payload: accountStatus,
})

export const getAccountStatusAction =
  (email: string): any =>
  async (dispatch: TAppDispatchThunk<any>): Promise<IAccountStatus | null> => {
    dispatch(startFetching(GET_ACCOUNT_STATUS))

    try {
      const response = await userApi.getAccountStatus(email)

      if (response.success && response.data?.data) {
        const accountStatus = getAccountStatusFromRawStatus(response.data.data)
        dispatch(setAccountStatusAction(accountStatus))

        return accountStatus
      }

      if (!response.success && response.status === 404) {
        const accountStatus: IAccountStatus = {
          existingUuid: '',
          hasAccount: false,
          hasAccessToApp: false,
        }
        dispatch(setAccountStatusAction(accountStatus))

        return accountStatus
      }

      dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
      return null
    } finally {
      dispatch(stopFetching(GET_ACCOUNT_STATUS))
    }
  }

export const resendInstructionsAction =
  (uuid: string): any =>
  async (dispatch: TAppDispatchThunk<any>): Promise<boolean> => {
    dispatch(startFetching(RESEND_INSTRUCTIONS))

    try {
      const response = await retenoEventsApi.sendRetenoEvent(
        uuid,
        RetenoEvents.RESEND_INSTRUCTIONS,
      )

      if (!response.success) {
        dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
        return false
      }

      return true
    } finally {
      dispatch(stopFetching(RESEND_INSTRUCTIONS))
    }
  }

export const setUUIDAndSendUserConfigAction =
  (uuid: string, config: TUserConfig): any =>
  async (dispatch: TAppDispatchThunk<any>): Promise<boolean> => {
    dispatch(setUUIDAction(uuid))
    dispatch(startFetching(SEND_USER_CONFIG))

    try {
      const response = await userApi.saveUserConfig({ uuid, config })

      if (!response.success) {
        dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
        return false
      }

      return true
    } finally {
      dispatch(stopFetching(SEND_USER_CONFIG))
    }
  }

export function setUserStatusAction(
  userStatus: IUserStatus,
): IAction<IUserStatus> {
  return {
    type: SET_STATUS,
    payload: userStatus,
  }
}

export function setTestEnvironmentQueryParamAction(
  testEnvironmentQueryParam: string,
): IAction<string> {
  return {
    type: SET_TEST_ENVIRONMENT_QUERY_PARAM,
    payload: testEnvironmentQueryParam,
  }
}

export function setAccountCreationFlowPendingAction(
  payload: boolean,
): IAction<boolean> {
  return {
    type: SET_ACCOUNT_CREATION_FLOW_PENDING,
    payload,
  }
}

export const setUpUUIDAction =
  ({
    cohort,
    uuid: uuidFromUrl,
    utmTags: utmTagsFromUrl,
    giaApiKey,
    language,
  }: {
    cohort: string
    uuid: string | null
    utmTags: TUtmTags | null
    giaApiKey: string
    language: string
  }): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuidFromStore = selectUUID(state)
    const uuid = uuidFromUrl || uuidFromStore
    const utmTagsFromStore = selectUtmTags(state)
    const utmTags = utmTagsFromUrl || utmTagsFromStore

    if (uuid) {
      dispatch(setUUIDAction(uuid))
      return
    }

    const response = await userApi.getUUID({
      cohort,
      appId: giaApiKey,
      locale: language,
      utm: utmTags,
    })

    if (response.success) {
      const responseUUID = (response.data as IGetUUIDResponseRaw).value
      dispatch(setUUIDAction(responseUUID))
    } else {
      console.error('error')

      if (response.status) {
        captureFunnelError(FunnelErrorCode.FUNNEL_UUID_CREATION_FAILED, null, {
          level: 'error',
          extra: { cohort, language, status: response.status },
        })
      }
    }
  }

export const sendFacebookParamsAction =
  (facebookParams?: IFacebookParams): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUUID(state)
    const { fbp, fbc } = facebookParams || {
      fbp: getCookie('_fbp'),
      fbc: getCookie('_fbc'),
    }

    dispatch(startFetching(SEND_FACEBOOK_PARAMS))

    const response = await userApi.saveFacebookParams({
      uuid,
      fbp,
      fbc,
    })

    if (!response.success) {
      dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
    }

    dispatch(stopFetching(SEND_FACEBOOK_PARAMS))
  }

export function bindUserAction(token: string): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<boolean> => {
    const state = getState()
    const uuid = selectUUID(state)

    dispatch(startFetching(BIND_USER))

    const response = await userApi.bindUser({ token, uuid })

    if (response.success && response.data) {
      const userStatusResponse = await userApi.getUserStatus(uuid)

      if (!userStatusResponse.success || !userStatusResponse.data?.state) {
        if (userStatusResponse.status) {
          captureFunnelError(
            FunnelErrorCode.ACCOUNT_STATUS_MISSING_AFTER_BIND,
            null,
            {
              level: 'error',
              extra: { status: userStatusResponse.status },
            },
          )
        }
        dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
        dispatch(stopFetching(BIND_USER))
        return false
      }

      const userStatus = getUserStatusFromRawUserStatus(
        userStatusResponse.data.state,
      )

      dispatch(setUserStatusAction(userStatus))
      eventLogger.logAccountCreated(userStatus.account.email)
    } else {
      eventLogger.logAccountCreationFailed(response?.data?.error)

      if (response.status === 409) {
        const errorMessage = response?.data?.error || ''
        const message = errorMessage.toLowerCase().includes('subscription')
          ? i18n.t('account.refunded')
          : i18n.t('account.emailExists')

        dispatch(setErrorAction(message))
        dispatch(stopFetching(BIND_USER))
        return false
      }

      dispatch(setErrorAction('Error: Something went wrong.'))
      dispatch(stopFetching(BIND_USER))
      return false
    }

    dispatch(stopFetching(BIND_USER))
    return true
  }
}

export function getUserStatusAction(uuid: string): TAppActionThunk<any> {
  return async (dispatch) => {
    dispatch(startFetching(GET_STATUS))

    const response = await userApi.getUserStatus(uuid)
    if (response.success && response.data) {
      const { state } = response.data

      const userStatus = getUserStatusFromRawUserStatus(state)
      dispatch(setUserStatusAction(userStatus))
      dispatch(setAnswersFromBackendAction(userStatus.onboarding))
      dispatch(setUtmTags(userStatus.config?.utmTags))
    }

    if (!response.success && response.status === 404) {
      const { search } = window.location
      const urlParams = new URLSearchParams(search)
      urlParams.delete('uuid')

      // use window.location.search to reload the page with updated query params
      window.location.search = `?${urlParams}`
    }

    dispatch(stopFetching(GET_STATUS))
  }
}

export const sendUserAnswersAction =
  (
    unsuccessCallback?: (() => void) | null,
    isFinished = false,
    isSkipped = false,
  ): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const answers = selectAnswers(state) as TAnswers
    const uuid = selectUUID(state)

    dispatch(startFetching(SEND_USER_ANSWERS))

    const response = await userApi.saveUserAnswers({
      uuid,
      answers,
      isFinished,
      isSkipped,
    })

    if (!response.success) {
      dispatch(setErrorAction(i18n.t('commonComponents.commonError')))

      unsuccessCallback && unsuccessCallback()
    }

    dispatch(stopFetching(SEND_USER_ANSWERS))
  }

export const sendUserConfigAction =
  (config: TUserConfig, unsuccessCallback?: (() => void) | null): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUUID(state)

    dispatch(startFetching(SEND_USER_CONFIG))

    try {
      const response = await userApi.saveUserConfig({
        uuid,
        config,
      })

      if (!response.success) {
        dispatch(setErrorAction(i18n.t('commonComponents.commonError')))

        unsuccessCallback && unsuccessCallback()
      }
    } finally {
      dispatch(stopFetching(SEND_USER_CONFIG))
    }
  }

export function updateUserConfigAction(
  value: TUserConfig,
): IAction<TUserConfig> {
  return {
    type: UPDATE_USER_CONFIG,
    payload: value,
  }
}

export const sendUserRDTCID =
  (rdtcid: string): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUUID(state)

    dispatch(startFetching(SEND_USER_RDTCID))

    try {
      const response = await userApi.saveUserRDTCID({ rdtcid, uuid })

      if (!response.success) {
        dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
      }

      dispatch(stopFetching(SEND_USER_RDTCID))
    } catch (error: unknown) {
      if (error instanceof Error) {
        dispatch(setErrorAction(error.toString()))
        dispatch(stopFetching(SEND_USER_RDTCID))

        return
      }

      dispatch(setErrorAction(String(error)))
      dispatch(stopFetching(SEND_USER_RDTCID))
    }
  }

export const sendUserTTCLID =
  (ttclid: string): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUUID(state)

    dispatch(startFetching(SEND_USER_TTCLID))

    try {
      const response = await userApi.saveUserTTCLID({ ttclid, uuid })

      if (!response.success) {
        dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
      }

      dispatch(stopFetching(SEND_USER_TTCLID))
    } catch (error: unknown) {
      if (error instanceof Error) {
        dispatch(setErrorAction(error.toString()))
        dispatch(stopFetching(SEND_USER_TTCLID))

        return
      }

      dispatch(setErrorAction(String(error)))
      dispatch(stopFetching(SEND_USER_TTCLID))
    }
  }

export const sendUserSCCID =
  (sccid: string, scid: string, unsuccessCallback?: () => void): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUUID(state)

    dispatch(startFetching(SEND_USER_SCCID))

    const response = await userApi.saveUserSCCID({ sccid, uuid, scid })

    if (!response.success) {
      dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
      unsuccessCallback && unsuccessCallback()
      dispatch(stopFetching(SEND_USER_SCCID))
      return
    }

    dispatch(stopFetching(SEND_USER_SCCID))
  }

export const sendUserEPIK =
  (epik: string, unsuccessCallback?: (() => void) | null): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUUID(state)

    dispatch(startFetching(SEND_USER_EPIK))

    const response = await userApi.saveUserEPIK({
      epik,
      uuid,
    })

    if (!response.success) {
      dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
      unsuccessCallback && unsuccessCallback()
      dispatch(stopFetching(SEND_USER_EPIK))
      return
    }

    dispatch(stopFetching(SEND_USER_EPIK))
  }

export const sendUserEmailAction =
  ({
    email,
    successCallback,
    unsuccessCallback,
  }: {
    email: string
    successCallback?: (() => void) | null
    unsuccessCallback?: (() => void) | null
  }): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUUID(state)
    const answers = selectAnswers(state)

    dispatch(startFetching(SEND_USER_EMAIL))

    const response = await userApi.saveUserEmail({
      uuid,
      email,
    })

    if (!response.success) {
      dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
      dispatch(stopFetching(SEND_USER_EMAIL))

      eventLogger.logEmailPageError(JSON.stringify(response.data))
      unsuccessCallback && unsuccessCallback()
      return
    }

    successCallback && successCallback()

    retenoLogger.logUserPersonalizedInfo({
      gender: answers?.[CustomPageId.GENDER],
      age: answers?.[CustomPageId.AGE],
    })

    window.ttq && window.ttq.identify({ email })
    window.ttq && window.ttq.track('CompleteRegistration', { event_id: uuid })

    window.axon &&
      window.axon('track', 'sign_up', {
        user_id: uuid,
        method: 'email',
      })

    window.snaptr &&
      window.snaptr('track', 'SIGN_UP', {
        user_email: email,
      })

    window.pintrk &&
      window.pintrk('track', 'lead', {
        external_id: getSha256Hash(uuid),
        ...(email && { em: getSha256Hash(email) }),
      })

    await dispatch(getUserStatusAction(uuid))
    dispatch(stopFetching(SEND_USER_EMAIL))
  }

export const sendUserEmailAndPasswordAction =
  ({ email, password }: { email: string; password: string }): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUUID(state)

    dispatch(startFetching(SEND_USER_LEARNWORLDS_ACCOUNT))

    const response = await userApi.saveUserEmailAndPassword({
      uuid,
      email,
      password,
    })

    if (!response.success) {
      dispatch(setErrorAction(i18n.t('commonComponents.commonErrorWithCTA')))

      eventLogger.logLearnWorldsAccountCreationFailed(
        response?.data?.error || i18n.t('commonComponents.commonError'),
      )

      if (response.status) {
        captureFunnelError(
          FunnelErrorCode.ACCOUNT_LEARNWORLDS_CREATION_FAILED,
          null,
          {
            level: 'error',
            extra: { status: response.status, error: response?.data?.error },
          },
        )
      }

      dispatch(stopFetching(SEND_USER_LEARNWORLDS_ACCOUNT))
      return
    }

    await dispatch(
      sendUserConfigAction({
        is_user_signed_up: true,
      }),
    )
    await dispatch(getUserStatusAction(uuid))

    eventLogger.logLearnWorldsAccountCreated(email)
    dispatch(stopFetching(SEND_USER_LEARNWORLDS_ACCOUNT))
  }

export const sendUserEmailConsentAction =
  ({ consented }: { consented: boolean }): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUUID(state)

    dispatch(startFetching(SEND_USER_EMAIL_CONSENT))

    const response = await userApi.saveUserEmailConsent({
      uuid,
      consented,
    })

    if (!response.success) {
      dispatch(setErrorAction(i18n.t('commonComponents.commonError')))

      dispatch(stopFetching(SEND_USER_EMAIL_CONSENT))
      return
    }

    dispatch(getUserStatusAction(uuid))

    dispatch(stopFetching(SEND_USER_EMAIL_CONSENT))
  }

export function setOneLinkAction(payload: string): IAction<string> {
  return {
    type: SET_ONE_LINK,
    payload,
  }
}

export function getOneLinkAction(unsuccessCallback?: () => void): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()
    const uuid = selectUUID(state)

    const outerLink = APP_OUTER_LINKS[APP_NAME]

    dispatch(startFetching(GET_ONE_LINK))

    const autologinTokenResponse = await userApi.getAutologinToken(uuid)

    if (!autologinTokenResponse.success || !autologinTokenResponse.data) {
      dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
      dispatch(setOneLinkAction(outerLink))
      dispatch(stopFetching(GET_ONE_LINK))
      unsuccessCallback && unsuccessCallback()
      return
    }

    const linkWithToken = addTokenToLink(
      outerLink,
      autologinTokenResponse.data.custom_token,
    )

    retenoLogger.logUserRedirectionLink({
      onelink: linkWithToken,
    })

    dispatch(setOneLinkAction(linkWithToken))
    dispatch(stopFetching(GET_ONE_LINK))
  }
}

export function setLearnworldsLinkAction(payload: string): IAction<string> {
  return {
    type: SET_LEARNWORLDS_LINK,
    payload,
  }
}

export function getLearnWorldsUserTokenAction(
  uuid: string,
  fallbackCourseLink: string,
): TAppActionThunk<any> {
  return async (dispatch) => {
    dispatch(startFetching(GET_LEARNWORLDS_LINK))

    const response = await userApi.getLearnWorldsUserToken(uuid)

    const { token } = response.data

    if (!response.success || !token) {
      dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
      dispatch(setLearnworldsLinkAction(fallbackCourseLink))
      dispatch(stopFetching(GET_LEARNWORLDS_LINK))

      return
    }

    const redirectionLink = `${LEARNWORLDS_DEEP_LINK_BASE}${token}`

    retenoLogger.logUserRedirectionLink({
      link: redirectionLink,
    })
    dispatch(setLearnworldsLinkAction(redirectionLink))

    dispatch(stopFetching(GET_LEARNWORLDS_LINK))
  }
}
